/* UI-DESIGN: LB-51 How it works in the homepage identity (clue-DESIGN.md):
   the dark lightbox room (LightboxSurface's static lit table, no three.js),
   a hard-edged glass intro, the night's steps typed on sheets in an open
   case file with one red pencil note, the homepage's "What's in the case
   file" (CaseFileBox) and the Host card. Static content; routes unchanged. */
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { ArrowRight, Magnifier, Persons } from "@gravity-ui/icons";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import CaseFileBox from "../../components/lightbox/CaseFileBox";
import styles from "./About.module.scss";

/* UI-DESIGN: LB-85 follows the existing invite, role and round controls.
   Character reveal is supported; a separate final-solution screen is not. */
const STEPS = [
  { title: "Pick a case", text: "Choose a mystery for your group and name your party.", more: "Check the player count before inviting everyone." },
  { title: "Invite your friends", text: "Copy the invite link. Guests open it and RSVP from their phone.", more: "The guest list stays open until you close invites." },
  { title: "Set the guest list", text: "Once everyone has replied, close invites and choose their roles.", more: "Role choices use spoiler-free labels." },
  { title: "Send the roles", text: "Send everyone their character. Unassigned guests get a random role.", more: "The host can play a character too." },
  { title: "Meet your character", text: "Open your character sheet and get to know your part in the story.", more: "Keep your private details to yourself." },
  { title: "Start the night", text: "When everyone is ready, the host starts round one.", more: "Read your round updates, talk to the other players, then let the host move the game on." },
] as const;

const noop = () => undefined;

const About = () => {
  const navigate = useNavigate();
  return (
    <LightboxSurface mode="static" onUnavailable={noop}>
      <main className={styles.page}>
        <section className={styles.row} aria-labelledby="lb-about-title">
          <GlassPanel className={clsx(styles.card, styles.introPanel)}>
            <h1 id="lb-about-title" className={clsx(styles.title, styles.pageTitle)}>
              How it works
            </h1>
            {/* UI-DESIGN: describe the supported night without promising a timed game. */}
            <p className={styles.sub}>From the first invite to your first round.</p>
          </GlassPanel>

          {/* UI-DESIGN: the steps are papers in an open case file, typed,
              each a little askew; the red pencil marks the host joining the game, once */}
          <div className={styles.stepsStage}>
            <div className={styles.file} data-lb-contrast="">
              <span className={styles.tab} aria-hidden />
              <ol className={styles.sheets}>
                {STEPS.map((step, i) => (
                  <li key={step.title} className={styles.sheet}>
                    <h2 className={styles.sheetTitle}>
                      {i === 3 ? (
                        <span className={styles.ringed}>
                          {step.title}
                          <svg className={styles.ring} viewBox="0 0 200 80" preserveAspectRatio="none" aria-hidden focusable="false">
                            <path d="M168 14 C 128 0, 40 2, 14 30 C -4 52, 34 76, 104 74 C 168 72, 196 52, 190 32 C 186 18, 168 10, 140 8" />
                          </svg>
                        </span>
                      ) : (
                        step.title
                      )}
                    </h2>
                    <p className={styles.sheetText}>{step.text}</p>
                    <p className={styles.sheetMore}>{step.more}</p>
                    {i === 3 ? <p className={styles.pencil}>yes, the host too</p> : null}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* UI-DESIGN: the homepage's case file chapter, mirrored (file left,
            card right) so the page alternates like the Home */}
        <section className={clsx(styles.row, styles.insideRow)} aria-labelledby="lb-about-inside-title">
          <div className={styles.insideStage}>
            <CaseFileBox />
          </div>
          <GlassPanel className={clsx(styles.card, styles.insideCard)}>
            <h2 id="lb-about-inside-title" className={styles.title}>
              What's in the case file
            </h2>
            <p className={styles.sub}>
              Your character sheet and round updates stay on your phone, ready when you need them.
            </p>
          </GlassPanel>
        </section>

        <section className={styles.row} aria-labelledby="lb-about-host-title">
          <GlassPanel className={clsx(styles.card, styles.hostCard)}>
            <h2 id="lb-about-host-title" className={styles.title}>
              Ready to host?
            </h2>
            <p className={styles.sub}>We'll handle the mystery. You bring the friends (and the snacks).</p>
            <div className={styles.actions}>
              <LightboxButton tone="primary" icon={<Persons />} trailing={<ArrowRight />} onPress={() => navigate("/create-game")}>
                Host a party
              </LightboxButton>
              <LightboxButton tone="secondary" icon={<Magnifier />} onPress={() => navigate("/mysteries")}>
                Browse mysteries
              </LightboxButton>
            </div>
          </GlassPanel>
        </section>
      </main>
    </LightboxSurface>
  );
};

export default About;
